import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { useFirebase } from '../../../context/FirebaseContext';
import { useJournalEnabled } from '../../../context/FeatureFlagContext';
import { getLibraryRules } from '../../../api/conceptgate';

import AccountSection from './AccountSection';
import AnalyticsSection from './AnalyticsSection';
import TradeSpecSection from './TradeSpecSection';
import ValidationPipeline from './ValidationPipeline';
import EditModeForm from './EditModeForm';
import EmptyState from './EmptyState';

import RulesManagement from './RulesManagement';
import RuleConfigModal from './RuleConfigModal';
import ModuleConfigModal from './ModuleConfigModal';
import SaveToJournalModal from './SaveToJournalModal';
import TradeHistoryPanel from './TradeHistoryPanel';
import MiniCalendar from './MiniCalendar';
import ValidationCallHistory from './ValidationCallHistory';
import UsageDashboard from './UsageDashboard';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';

import './ConceptGatePanel.css';
import { isCodexUi } from '../../../utils/codexEnv';

const ConceptGatePanel = ({ isDarkMode, onTogglePanel, onOpenSettings, selectedInstrument, selectedCategory, currentPrice, onShowJournal, onShowHistory }) => {
  const userInfo = useSelector((state) => state.user.userInfo);
  const isAdmin = userInfo?.admin || false;
  const { auth } = useFirebase();
  const journalEnabled = useJournalEnabled();
  const {
    account,

    currentTrade,
    lastResult,
    validate,
    validateStreaming,
    cancelValidation,
    validating,
    streamingGates,
    streamingProgress,
    useStreamingValidation,
    getStatus,
    editMode,
    setEditMode,
    loading,
    error,
    clearTrade,
    setSelectedInstrument,
    setTradeSpec,
    userRules,
    rulesManagementOpen,
    openRulesManagement,
    closeRulesManagement,
    subscriptionStatus,
    undoQueue,
    undoRuleRemovalById,
    dismissUndoItem,
  } = useConceptGate();

  const [libraryRules, setLibraryRules] = useState([]);

  // Load library rules for the selected instrument
  useEffect(() => {
    if (!auth?.currentUser || !selectedInstrument) {
      setLibraryRules([]);
      return;
    }

    const normalizedInst = selectedInstrument.replace('_', '').toUpperCase();

    getLibraryRules(auth, (libraryData) => {
      if (libraryData && !libraryData.error) {
        // Filter rules that apply to this instrument or are universal (null instrument)
        const applicableRules = libraryData.filter(
          (rule) => !rule.instrument || rule.instrument === normalizedInst
        );
        setLibraryRules(applicableRules);
      } else {
        setLibraryRules([]);
      }
    }, { instrument: normalizedInst });
  }, [auth, selectedInstrument]);

  // Sync selected instrument with context
  React.useEffect(() => {
    if (selectedInstrument && setSelectedInstrument && !loading) {
      setSelectedInstrument(selectedInstrument, selectedCategory);
    }
  }, [selectedInstrument, selectedCategory, setSelectedInstrument, loading]);

  const status = getStatus();
  const hasTradeData = currentTrade !== null;

  const handleValidate = () => {
    if (isCodexUi) {
      return;
    }
    if (!validating && currentTrade) {
      // Use streaming validation by default for real-time gate progress
      if (useStreamingValidation) {
        validateStreaming();
      } else {
        validate();
      }
    }
  };

  const [saveJournalModalOpen, setSaveJournalModalOpen] = useState(false);
  const [journalEntrySaved, setJournalEntrySaved] = useState(false);
  const [journalRefreshTrigger, setJournalRefreshTrigger] = useState(0);

  // Accordion state management - only one section expanded at a time
  const ACCORDION_STATE_KEY = 'ntb:conceptgate:accordionExpanded';
  const [expandedSection, setExpandedSection] = useState(() => {
    const saved = localStorage.getItem(ACCORDION_STATE_KEY);
    return saved || null; // null means all collapsed, or 'account', 'trade', 'validation', 'history'
  });

  // API Call History visibility toggle
  const API_HISTORY_VISIBLE_KEY = 'ntb:conceptgate:apiHistoryVisible';
  const [apiHistoryVisible, setApiHistoryVisible] = useState(() => {
    const saved = localStorage.getItem(API_HISTORY_VISIBLE_KEY);
    return saved === 'true';
  });

  // Persist API history visibility
  useEffect(() => {
    localStorage.setItem(API_HISTORY_VISIBLE_KEY, apiHistoryVisible ? 'true' : 'false');
  }, [apiHistoryVisible]);

  // Persist accordion state
  useEffect(() => {
    localStorage.setItem(ACCORDION_STATE_KEY, expandedSection || '');
  }, [expandedSection]);

  // Handle section toggle - accordion behavior (only one open at a time)
  const handleSectionToggle = (sectionKey) => {
    setExpandedSection(prev => {
      // If clicking the same section, collapse it
      if (prev === sectionKey) {
        return null;
      }
      // Otherwise, expand the clicked section (others auto-collapse)
      return sectionKey;
    });
  };

  const openSaveJournalModal = () => setSaveJournalModalOpen(true);

  const closeSaveJournalModal = (success = false) => {
    setSaveJournalModalOpen(false);
    if (success === true) {
      setJournalEntrySaved(true);
      setJournalRefreshTrigger(prev => prev + 1);
    }
  };

  // Reset saved state when validation result changes (new validation run)
  useEffect(() => {
    setJournalEntrySaved(false);
  }, [lastResult]);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleSaveEdit = () => {
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className={`conceptgate-panel-container ${isDarkMode ? 'dark' : ''}`}>

        <div className="conceptgate-panel-content">
          <div className="conceptgate-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`conceptgate-panel-container ${isDarkMode ? 'dark' : ''}`}>
      {/* Header */}


      {/* Content */}
      <div className="conceptgate-panel-content">
        {error && (
          <div className={`conceptgate-error${error?.code === 'API_KEY_REQUIRED' ? ' api-key-error' : ''}`}>
            {error?.code === 'API_KEY_REQUIRED' ? (
              <>
                <span>{error.message}</span>
                <span className="conceptgate-error-hint">
                  Open the user menu (top-right) and click "API Keys" to add your key.
                </span>
              </>
            ) : (
              typeof error === 'string' ? error : error?.message || 'An error occurred'
            )}
          </div>
        )}

        {/* Account Section - Always visible and editable */}
        <AccountSection
          account={account}
          onEdit={onOpenSettings}
          editable
          isExpanded={expandedSection === 'account'}
          onToggle={() => handleSectionToggle('account')}
        />

        {/* Usage/Subscription Dashboard */}
        <UsageDashboard
          subscriptionStatus={subscriptionStatus}
          onShowHistory={onShowHistory || (() => setApiHistoryVisible(!apiHistoryVisible))}
        />

        {/* Trade Section - Common input for both Analytics and Validation */}
        {!hasTradeData && !editMode && (
          <EmptyState
            onCreateTrade={() => setEditMode(true)}
            onLoadSampleTrade={(sampleTrade) => {
              setTradeSpec(sampleTrade);
            }}
            selectedInstrument={selectedInstrument}
            currentPrice={currentPrice}
          />
        )}

        {editMode ? (
          <EditModeForm
            onCancel={handleCancelEdit}
            onSave={handleSaveEdit}
          />
        ) : hasTradeData && (
          <TradeSpecSection
            trade={currentTrade}
            onEdit={handleEdit}
            onDelete={clearTrade}
            isExpanded={expandedSection === 'trade'}
            onToggle={() => handleSectionToggle('trade')}
          />
        )}

        {/* Analytics Section - Market Direction Analysis (has its own pipeline selector) */}
        <AnalyticsSection
          isExpanded={expandedSection === 'analytics'}
          onToggle={() => handleSectionToggle('analytics')}
          selectedInstrument={selectedInstrument}
          subscriptionStatus={subscriptionStatus}
        />

        {/* Validation Pipeline - Rule-based trade validation */}
        <ValidationPipeline
          gates={validating && streamingGates.length > 0 ? streamingGates : lastResult?.gates}
          status={status}
          validationResult={lastResult}
          userRules={userRules || []}
          libraryRules={libraryRules}
          onOpenRulesManagement={openRulesManagement}
          onValidate={handleValidate}
          onSaveToJournal={journalEnabled ? openSaveJournalModal : null}
          isJournalSaved={journalEntrySaved}
          onCancelValidation={cancelValidation}
          validating={validating}
          hasTrade={hasTradeData}
          streamingProgress={streamingProgress}
          subscriptionStatus={subscriptionStatus}
          isLoading={loading}
          isAdmin={isAdmin}
          isExpanded={expandedSection === 'validation'}
          onToggle={() => handleSectionToggle('validation')}
        />

        {/* Trade History Panel - Quick access to past trades */}
        {journalEnabled && (
          <TradeHistoryPanel
            onViewAll={() => {
              if (onShowJournal) onShowJournal();
              else window.location.href = '/trader-journal';
            }}
            compact
            limit={5}
            refreshTrigger={journalRefreshTrigger}
            isExpanded={expandedSection === 'history'}
            onToggle={() => handleSectionToggle('history')}
            selectedInstrument={selectedInstrument}
          />
        )}

        {/* Mini Calendar - P&L overview */}
        {journalEnabled && (
          <MiniCalendar
            onViewAll={() => {
              if (onShowJournal) onShowJournal();
              else window.location.href = '/trader-journal';
            }}
            refreshTrigger={journalRefreshTrigger}
            isExpanded={expandedSection === 'calendar'}
            onToggle={() => handleSectionToggle('calendar')}
          />
        )}

        {/* API Call History - Shows UI, MCP, and Webhook calls */}
        {apiHistoryVisible && <ValidationCallHistory />}
      </div>

      <div className="conceptgate-footer-disclaimer">
        NanoTradingCopilot is not a financial advisor. All validation checks are based on your own configured rules. Trading involves significant risk.
        <br />
        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      </div>

      {/* Rules Management Modal */}
      <RulesManagement
        open={rulesManagementOpen}
        onClose={closeRulesManagement}
        libraryRules={libraryRules}
      />

      {/* Rule Config Modal */}
      <RuleConfigModal />

      {/* Module Config Modal */}
      <ModuleConfigModal />

      <SaveToJournalModal
        open={saveJournalModalOpen}
        onClose={closeSaveJournalModal}
        trade={currentTrade}
        validationResult={lastResult}
        account={account}
      />

      {/* Undo Toast Stack */}
      {undoQueue && undoQueue.length > 0 && (
        <div className="undo-toast-stack">
          {undoQueue.map((undoItem, index) => (
            <div
              key={undoItem.ruleId}
              className="undo-toast"
              style={{ transform: `translateY(${-index * 8}px)`, zIndex: 100 - index }}
            >
              <span className="undo-toast-message">{undoItem.message}</span>
              <button
                className="undo-toast-btn"
                onClick={() => undoRuleRemovalById(undoItem.ruleId)}
              >
                <UndoIcon fontSize="small" />
                Undo
              </button>
              <button
                className="undo-toast-close"
                onClick={() => dismissUndoItem(undoItem.ruleId)}
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConceptGatePanel;
